import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ImagePreviewGrid = ({ 
  images = [], 
  onRemoveImage, 
  onReorderImages,
  isProcessing = false 
}) => {
  const handleRemove = (index) => {
    if (onRemoveImage && !isProcessing) {
      onRemoveImage(index);
    }
  };

  const handleMoveUp = (index) => {
    if (index > 0 && onReorderImages && !isProcessing) {
      const newImages = [...images];
      [newImages[index - 1], newImages[index]] = [newImages?.[index], newImages?.[index - 1]];
      onReorderImages(newImages);
    }
  };

  const handleMoveDown = (index) => {
    if (index < images?.length - 1 && onReorderImages && !isProcessing) {
      const newImages = [...images];
      [newImages[index], newImages[index + 1]] = [newImages?.[index + 1], newImages?.[index]];
      onReorderImages(newImages);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  if (images?.length === 0) {
    return null;
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">
          Selected Photos ({images?.length})
        </h3>
        {images?.length > 1 && !isProcessing && (
          <p className="text-sm text-muted-foreground">
            Drag to reorder or use arrow buttons
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {images?.map((image, index) => (
          <div
            key={`${image?.name}-${index}`}
            className={`relative bg-card border border-border rounded-lg overflow-hidden group ${
              isProcessing ? 'opacity-60' : ''
            }`}
          >
            {/* Image Preview */}
            <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden">
              {image?.preview ? (
                <Image
                  src={image?.preview}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center space-y-2 text-muted-foreground">
                  <Icon name="FileImage" size={32} />
                  <span className="text-sm">Loading preview...</span>
                </div>
              )}
            </div>

            {/* Image Info Overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
              <div className="text-white">
                <p className="text-sm font-medium truncate">{image?.name}</p>
                <p className="text-xs opacity-80">{formatFileSize(image?.size)}</p>
              </div>
            </div>

            {/* Primary Photo Badge */}
            {index === 0 && (
              <div className="absolute top-2 left-2">
                <div className="bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-medium">
                  Primary
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {!isProcessing && (
              <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Reorder Buttons */}
                {images?.length > 1 && (
                  <>
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className={`p-1 rounded-full transition-colors ${
                        index === 0
                          ? 'bg-black/20 text-white/40 cursor-not-allowed' :'bg-black/40 text-white hover:bg-black/60'
                      }`}
                      aria-label="Move up"
                    >
                      <Icon name="ChevronUp" size={14} />
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === images?.length - 1}
                      className={`p-1 rounded-full transition-colors ${
                        index === images?.length - 1
                          ? 'bg-black/20 text-white/40 cursor-not-allowed' :'bg-black/40 text-white hover:bg-black/60'
                      }`}
                      aria-label="Move down"
                    >
                      <Icon name="ChevronDown" size={14} />
                    </button>
                  </>
                )}
                
                {/* Remove Button */}
                <button
                  onClick={() => handleRemove(index)}
                  className="p-1 bg-error/80 text-white rounded-full hover:bg-error transition-colors"
                  aria-label="Remove image"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>
            )}

            {/* Processing Overlay */}
            {isProcessing && (
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                <div className="bg-white/90 rounded-full p-2">
                  <Icon name="Loader2" size={20} className="animate-spin text-accent" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Upload Tips */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Lightbulb" size={20} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2">Photo Tips for Better Analysis</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Take photos in good lighting conditions</li>
              <li>• Include multiple angles: side view, drivetrain, and cockpit</li>
              <li>• Ensure components are clearly visible and unobstructed</li>
              <li>• The first photo will be used as the primary reference</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImagePreviewGrid;